import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Search, Filter, Users, UserPlus, Eye, UserX, UserCheck,
  MoreVertical, RefreshCw, Download, ChevronLeft, ChevronRight,
  Gift, Trash2, LogIn, Mail, Calendar, Activity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Filters & Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Modals
  const [grantAccessModal, setGrantAccessModal] = useState(false);
  const [grantForm, setGrantForm] = useState({ email: '', freeUntil: '', reason: 'Beta tester' });
  const [bulkActionModal, setBulkActionModal] = useState(false);

  const adminKey = sessionStorage.getItem('aura_admin_key');

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter, statusFilter, sortBy]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pageSize,
        skip: (page - 1) * pageSize,
        sort: sortBy,
        ...(roleFilter !== 'all' && { role: roleFilter }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(searchQuery && { search: searchQuery })
      });

      const response = await axios.get(`${API}/api/admin/users?${params}`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      
      setUsers(response.data.users || []);
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const toggleUserSelection = (userId) => {
    if (!userId) return;
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllVisible = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.user_id || u._id || u.id).filter(Boolean));
    }
  };

  const handleDisableUser = async (email) => {
    if (!window.confirm(`Disable account for ${email}?`)) return;
    
    try {
      await axios.post(`${API}/api/admin/disable-account`, 
        { email, reason: 'Disabled by admin' },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      toast.success('Account disabled');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to disable account');
    }
  };

  const handleEnableUser = async (email) => {
    try {
      await axios.post(`${API}/api/admin/enable-account`, 
        { email },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      toast.success('Account enabled');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to enable account');
    }
  };

  const handleGrantFreeAccess = async () => {
    if (!grantForm.email) {
      toast.error('Email is required');
      return;
    }
    
    try {
      await axios.post(`${API}/api/admin/grant-free-access`, 
        {
          email: grantForm.email,
          reason: grantForm.reason,
          free_until: grantForm.freeUntil || null
        },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      
      toast.success(`Free access granted to ${grantForm.email}`);
      setGrantAccessModal(false);
      setGrantForm({ email: '', freeUntil: '', reason: 'Beta tester' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const response = await axios.post(`${API}/api/admin/impersonate`, 
        { user_id: userId },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      
      // Store impersonation data
      localStorage.setItem('aura_token', response.data.token);
      localStorage.setItem('aura_impersonating', 'true');
      localStorage.setItem('aura_admin_key_backup', adminKey);
      
      toast.success('Impersonation started - you are now viewing as this user');
      window.open('/', '_blank');
    } catch (error) {
      toast.error('Failed to impersonate user');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }

    try {
      await axios.post(`${API}/api/admin/bulk-action`, 
        { user_ids: selectedUsers, action },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      
      toast.success(`Bulk ${action} completed for ${selectedUsers.length} users`);
      setSelectedUsers([]);
      setBulkActionModal(false);
      fetchUsers();
    } catch (error) {
      toast.error('Bulk action failed');
    }
  };

  const totalPages = Math.ceil(totalUsers / pageSize);

  const getRoleBadge = (role) => {
    const styles = {
      athlete: 'bg-aura-primary/20 text-aura-primary',
      supporter: 'bg-purple-500/20 text-purple-400',
      org_admin: 'bg-amber-500/20 text-amber-400'
    };
    return styles[role] || 'bg-white/10 text-white';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight">
              User Management
            </h1>
            <p className="text-aura-text-muted text-sm mt-1">
              {totalUsers} total users
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={grantAccessModal} onOpenChange={setGrantAccessModal}>
              <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700 text-white">
                  <Gift className="w-4 h-4 mr-2" />
                  Grant Access
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-white font-heading uppercase">
                    Grant Free Access
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-xs text-aura-text-muted uppercase">Email</label>
                    <Input
                      value={grantForm.email}
                      onChange={(e) => setGrantForm({ ...grantForm, email: e.target.value })}
                      className="bg-aura-bg border-white/10 text-white mt-1"
                      placeholder="user@example.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-aura-text-muted uppercase">Reason</label>
                    <Input
                      value={grantForm.reason}
                      onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })}
                      className="bg-aura-bg border-white/10 text-white mt-1"
                      placeholder="Beta tester"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-aura-text-muted uppercase">Free Until (optional)</label>
                    <Input
                      type="date"
                      value={grantForm.freeUntil}
                      onChange={(e) => setGrantForm({ ...grantForm, freeUntil: e.target.value })}
                      className="bg-aura-bg border-white/10 text-white mt-1"
                    />
                  </div>
                  <Button onClick={handleGrantFreeAccess} className="w-full bg-green-600 hover:bg-green-700">
                    Grant Access
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="outline" className="border-white/10 text-white" onClick={() => fetchUsers()}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aura-text-muted" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="pl-10 bg-aura-bg border-white/10 text-white"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-40 bg-aura-bg border-white/10 text-white">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent className="bg-aura-surface border-white/10">
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="athlete">Athletes</SelectItem>
                  <SelectItem value="supporter">Supporters</SelectItem>
                  <SelectItem value="org_admin">Org Admins</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-aura-bg border-white/10 text-white">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-aura-surface border-white/10">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                  <SelectItem value="free_access">Free Access</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48 bg-aura-bg border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-aura-surface border-white/10">
                  <SelectItem value="created_at_desc">Newest First</SelectItem>
                  <SelectItem value="created_at_asc">Oldest First</SelectItem>
                  <SelectItem value="last_active_desc">Recently Active</SelectItem>
                  <SelectItem value="name_asc">Name A-Z</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" className="bg-aura-primary hover:bg-aura-primary-hover">
                <Filter className="w-4 h-4 mr-2" />
                Apply
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-amber-400">
                {selectedUsers.length} user(s) selected
              </span>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-amber-500/50 text-amber-400"
                  onClick={() => handleBulkAction('grant_free_access')}
                >
                  <Gift className="w-4 h-4 mr-1" /> Grant Access
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-red-500/50 text-red-400"
                  onClick={() => handleBulkAction('disable')}
                >
                  <UserX className="w-4 h-4 mr-1" /> Disable
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setSelectedUsers([])}
                  className="text-aura-text-muted"
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users Table */}
        <Card className="bg-aura-surface border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="p-4 text-left">
                    <Checkbox 
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onCheckedChange={selectAllVisible}
                    />
                  </th>
                  <th className="p-4 text-left text-xs text-aura-text-muted uppercase tracking-wider">User</th>
                  <th className="p-4 text-left text-xs text-aura-text-muted uppercase tracking-wider">Role</th>
                  <th className="p-4 text-left text-xs text-aura-text-muted uppercase tracking-wider">Status</th>
                  <th className="p-4 text-left text-xs text-aura-text-muted uppercase tracking-wider">Joined</th>
                  <th className="p-4 text-left text-xs text-aura-text-muted uppercase tracking-wider">Last Active</th>
                  <th className="p-4 text-left text-xs text-aura-text-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center">
                      <RefreshCw className="w-6 h-6 text-aura-primary animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-aura-text-muted">
                      No users found
                    </td>
                  </tr>
                ) : users.map((user) => (
                  <tr key={user.user_id || user._id || user.id} className="hover:bg-white/5 transition-colors">
                    <td className="p-4">
                      <Checkbox 
                        checked={selectedUsers.includes(user.user_id || user._id || user.id)}
                        onCheckedChange={() => toggleUserSelection(user.user_id || user._id || user.id)}
                      />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-aura-primary to-aura-secondary flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-sm">
                            {(user.name || user.email)?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name || 'No name'}</p>
                          <p className="text-xs text-aura-text-muted">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={`${getRoleBadge(user.role)} border-0`}>
                        {user.role}
                      </Badge>
                    </td>
                    <td className="p-4">
                      {user.disabled ? (
                        <Badge className="bg-red-500/20 text-red-400 border-0">Disabled</Badge>
                      ) : user.free_access ? (
                        <Badge className="bg-green-500/20 text-green-400 border-0">Free Access</Badge>
                      ) : (
                        <Badge className="bg-white/10 text-white border-0">Active</Badge>
                      )}
                    </td>
                    <td className="p-4 text-aura-text-muted text-sm">
                      {user.created_at ? format(parseISO(user.created_at), 'MMM d, yyyy') : '-'}
                    </td>
                    <td className="p-4 text-aura-text-muted text-sm">
                      {user.last_active ? format(parseISO(user.last_active), 'MMM d, h:mm a') : 'Never'}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <Link to={`/admin/users/${user.user_id || user._id || user.id}`}>
                          <Button size="sm" variant="ghost" className="text-aura-text-muted hover:text-white">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-aura-text-muted hover:text-aura-primary"
                          onClick={() => handleImpersonate(user.user_id || user._id || user.id)}
                          title="Login as user"
                        >
                          <LogIn className="w-4 h-4" />
                        </Button>
                        {user.disabled ? (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-green-400 hover:text-green-300"
                            onClick={() => handleEnableUser(user.email)}
                          >
                            <UserCheck className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-400 hover:text-red-300"
                            onClick={() => handleDisableUser(user.email)}
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-white/10 flex items-center justify-between">
            <p className="text-sm text-aura-text-muted">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalUsers)} of {totalUsers}
            </p>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="border-white/10 text-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-white text-sm px-3">
                Page {page} of {totalPages}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="border-white/10 text-white"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminUsersPage;
